import { useCallback, useRef, useState } from "react";

export class ReauthRequiredError extends Error {
  constructor() {
    super("reauthRequired");
    this.name = "ReauthRequiredError";
  }
}

export async function fetchWithReauthToken<T>(
  url: string,
  method: string,
  body: unknown,
  reauthToken: string,
): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Reauth-Token": reauthToken,
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    const data: { reauthRequired?: boolean } = await res.json().catch(() => ({}));
    if (data.reauthRequired) throw new ReauthRequiredError();
  }

  if (!res.ok) {
    const err: { error?: string } = await res
      .json()
      .catch(() => ({ error: `API error: ${res.status}` }));
    throw new Error(err.error ?? `API error: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export function useReauth() {
  const [reauthToken, setReauthToken] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingFn = useRef<((token: string) => void) | null>(null);

  const withReauth = useCallback(
    (fn: (token: string) => void): void => {
      if (reauthToken) {
        fn(reauthToken);
      } else {
        pendingFn.current = fn;
        setDialogOpen(true);
      }
    },
    [reauthToken],
  );

  const onVerified = useCallback((token: string) => {
    setReauthToken(token);
    setDialogOpen(false);
    pendingFn.current?.(token);
    pendingFn.current = null;
  }, []);

  const onDialogOpenChange = useCallback((open: boolean) => {
    if (!open) pendingFn.current = null;
    setDialogOpen(open);
  }, []);

  const clearToken = useCallback(() => {
    setReauthToken(null);
  }, []);

  return { dialogOpen, onDialogOpenChange, withReauth, onVerified, clearToken };
}
